import React, { useState } from 'react';
import { X, Send, Camera, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BUG_TYPES = [
  'UI Issue',
  'Data Error',
  'Feature Not Working',
  'Performance',
  'Login/Auth Issue',
  'Other'
];

const PRIORITIES = ['Low', 'Medium', 'High', 'Critical'];

const BugReportWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    bug_type: '',
    priority: 'Medium',
    description: '',
    steps_to_reproduce: '',
    screenshot: ''
  });

  const getBrowserInfo = () => {
    const ua = navigator.userAgent;
    const screen = `${window.screen.width}x${window.screen.height}`;
    const viewport = `${window.innerWidth}x${window.innerHeight}`;
    return `${ua} | Screen: ${screen} | Viewport: ${viewport}`;
  };

  const getCurrentPage = () => {
    const path = window.location.pathname;
    const hash = window.location.hash;
    return `${path}${hash}`;
  };

  const handleScreenshot = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Screenshot must be under 5MB');
      return;
    }
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('crewToken');
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/inspections/upload-photo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setForm(prev => ({ ...prev, screenshot: res.data.path }));
      toast.success('Screenshot attached');
    } catch {
      toast.error('Failed to upload screenshot');
    }
  };

  const handleSubmit = async () => {
    if (!form.description.trim()) {
      toast.error('Please describe the issue');
      return;
    }
    if (!form.bug_type) {
      toast.error('Please select a bug type');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('crewToken');
      await axios.post(`${API}/bug-reports`, {
        ...form,
        page: getCurrentPage(),
        browser_info: getBrowserInfo()
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setSent(true);
      toast.success('Bug report submitted! Thank you for helping improve the app.');
      setTimeout(() => {
        setIsOpen(false);
        setSent(false);
        setForm({ bug_type: '', priority: 'Medium', description: '', steps_to_reproduce: '', screenshot: '' });
      }, 2000);
    } catch {
      toast.error('Failed to submit bug report');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating Ladybug Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-20 right-5 z-50 w-16 h-16 transition-all hover:scale-110 active:scale-95 flex items-center justify-center drop-shadow-[0_4px_12px_rgba(0,0,0,0.3)] hover:drop-shadow-[0_6px_20px_rgba(0,0,0,0.4)]"
        style={{ background: 'none', border: 'none' }}
        title="Report a Bug"
        data-testid="bug-report-fab"
      >
        {/* Ladybug SVG — standalone, no circle background */}
        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          {/* Shadow */}
          <ellipse cx="12" cy="20" rx="6" ry="1.2" fill="#000" opacity="0.15"/>
          {/* Body */}
          <ellipse cx="12" cy="13.5" rx="7.8" ry="7" fill="#dc2626"/>
          {/* Body shine */}
          <ellipse cx="10" cy="10.5" rx="3" ry="2" fill="#ef4444" opacity="0.5"/>
          {/* Center line */}
          <line x1="12" y1="7" x2="12" y2="20.5" stroke="#1a1a1a" strokeWidth="1.2"/>
          {/* Spots */}
          <circle cx="9.2" cy="10.8" r="1.4" fill="#1a1a1a"/>
          <circle cx="14.8" cy="10.8" r="1.4" fill="#1a1a1a"/>
          <circle cx="8.8" cy="14.5" r="1.1" fill="#1a1a1a"/>
          <circle cx="15.2" cy="14.5" r="1.1" fill="#1a1a1a"/>
          <circle cx="9.8" cy="17.8" r="0.9" fill="#1a1a1a"/>
          <circle cx="14.2" cy="17.8" r="0.9" fill="#1a1a1a"/>
          {/* Head */}
          <circle cx="12" cy="7" r="3" fill="#1a1a1a"/>
          {/* Eyes */}
          <circle cx="10.8" cy="6.2" r="0.8" fill="white" opacity="0.9"/>
          <circle cx="13.2" cy="6.2" r="0.8" fill="white" opacity="0.9"/>
          <circle cx="11" cy="6" r="0.35" fill="#1a1a1a"/>
          <circle cx="13.4" cy="6" r="0.35" fill="#1a1a1a"/>
          {/* Antennae */}
          <path d="M10.2 4.5 Q8.5 1.5 6.5 1" stroke="#1a1a1a" strokeWidth="0.9" strokeLinecap="round" fill="none"/>
          <path d="M13.8 4.5 Q15.5 1.5 17.5 1" stroke="#1a1a1a" strokeWidth="0.9" strokeLinecap="round" fill="none"/>
          <circle cx="6.2" cy="0.8" r="0.8" fill="#1a1a1a"/>
          <circle cx="17.8" cy="0.8" r="0.8" fill="#1a1a1a"/>
          {/* Legs */}
          <path d="M5 11 Q3.5 10 2.5 9.5" stroke="#1a1a1a" strokeWidth="0.7" strokeLinecap="round" fill="none"/>
          <path d="M19 11 Q20.5 10 21.5 9.5" stroke="#1a1a1a" strokeWidth="0.7" strokeLinecap="round" fill="none"/>
          <path d="M4.5 14 Q3 14.5 2 15" stroke="#1a1a1a" strokeWidth="0.7" strokeLinecap="round" fill="none"/>
          <path d="M19.5 14 Q21 14.5 22 15" stroke="#1a1a1a" strokeWidth="0.7" strokeLinecap="round" fill="none"/>
          <path d="M5.5 17 Q4 18 3 19" stroke="#1a1a1a" strokeWidth="0.7" strokeLinecap="round" fill="none"/>
          <path d="M18.5 17 Q20 18 21 19" stroke="#1a1a1a" strokeWidth="0.7" strokeLinecap="round" fill="none"/>
        </svg>
      </button>

      {/* Bug Report Panel */}
      {isOpen && (
        <div className="fixed bottom-36 right-6 z-50 w-[340px] sm:w-[380px] bg-white rounded-2xl shadow-2xl border border-stone-200 overflow-hidden animate-in slide-in-from-bottom-4 duration-200" data-testid="bug-report-panel">
          {/* Header */}
          <div className="bg-gradient-to-r from-red-600 to-red-700 px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <ellipse cx="12" cy="13" rx="7" ry="6" fill="white" opacity="0.3"/>
                <circle cx="12" cy="6.5" r="2" fill="white" opacity="0.5"/>
              </svg>
              <h3 className="text-white font-bold text-lg">Report a Bug</h3>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white p-1" data-testid="bug-report-close">
              <X className="h-5 w-5" />
            </button>
          </div>

          {sent ? (
            <div className="p-8 text-center">
              <div className="text-4xl mb-3">&#10004;</div>
              <p className="font-bold text-stone-800 text-lg">Thank you!</p>
              <p className="text-stone-500 text-sm mt-1">Your report has been sent to the admin team.</p>
            </div>
          ) : (
            <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Auto-detected page */}
              <div className="text-xs text-stone-400 bg-stone-50 rounded-lg px-3 py-2">
                Page: <span className="text-stone-600 font-mono">{getCurrentPage()}</span>
              </div>

              {/* Bug Type */}
              <div>
                <label className="text-sm font-medium text-stone-700 block mb-1.5">What type of issue? *</label>
                <div className="relative">
                  <select
                    value={form.bug_type}
                    onChange={(e) => setForm(prev => ({ ...prev, bug_type: e.target.value }))}
                    className="w-full h-10 rounded-lg border border-stone-200 bg-white px-3 text-sm appearance-none cursor-pointer focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    data-testid="bug-type-select"
                  >
                    <option value="">Select type...</option>
                    {BUG_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" />
                </div>
              </div>

              {/* Priority */}
              <div>
                <label className="text-sm font-medium text-stone-700 block mb-1.5">Priority</label>
                <div className="flex gap-2">
                  {PRIORITIES.map(p => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setForm(prev => ({ ...prev, priority: p }))}
                      className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${
                        form.priority === p
                          ? p === 'Critical' ? 'bg-red-600 text-white'
                          : p === 'High' ? 'bg-orange-500 text-white'
                          : p === 'Medium' ? 'bg-amber-500 text-white'
                          : 'bg-blue-500 text-white'
                          : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                      }`}
                      data-testid={`priority-${p.toLowerCase()}`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="text-sm font-medium text-stone-700 block mb-1.5">Describe the issue *</label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="What happened? What did you expect?"
                  className="min-h-[80px] text-sm resize-none"
                  data-testid="bug-description"
                />
              </div>

              {/* Steps to Reproduce */}
              <div>
                <label className="text-sm font-medium text-stone-700 block mb-1.5">Steps to reproduce <span className="text-stone-400">(optional)</span></label>
                <Textarea
                  value={form.steps_to_reproduce}
                  onChange={(e) => setForm(prev => ({ ...prev, steps_to_reproduce: e.target.value }))}
                  placeholder="1. Go to...&#10;2. Click on...&#10;3. See error"
                  className="min-h-[60px] text-sm resize-none"
                  data-testid="bug-steps"
                />
              </div>

              {/* Screenshot */}
              <div>
                <label className="text-sm font-medium text-stone-700 block mb-1.5">Attach screenshot <span className="text-stone-400">(optional)</span></label>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleScreenshot}
                    className="hidden"
                    id="bug-screenshot"
                  />
                  <label htmlFor="bug-screenshot" className="cursor-pointer">
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      form.screenshot 
                        ? 'bg-green-50 text-green-700 border border-green-200' 
                        : 'bg-stone-100 text-stone-600 hover:bg-stone-200 border border-stone-200'
                    }`}>
                      <Camera className="h-4 w-4" />
                      {form.screenshot ? 'Screenshot attached' : 'Add screenshot'}
                    </div>
                  </label>
                  {form.screenshot && (
                    <button
                      type="button"
                      onClick={() => setForm(prev => ({ ...prev, screenshot: '' }))}
                      className="text-stone-400 hover:text-red-500"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Submit */}
              <Button
                onClick={handleSubmit}
                disabled={sending}
                className="w-full bg-red-600 hover:bg-red-700 text-white rounded-xl h-11 font-bold"
                data-testid="submit-bug-report"
              >
                {sending ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    Sending...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Send className="h-4 w-4" />
                    Submit Report
                  </div>
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default BugReportWidget;
